export const BOARD_STATE_VALUES = ['all', 'installed', 'available'] as const
export type BoardState = (typeof BOARD_STATE_VALUES)[number]

export const BOARD_ORDERING_VALUES = [
  'name',
  '-name',
  'newest',
  'oldest'
] as const
export type BoardOrdering = (typeof BOARD_ORDERING_VALUES)[number]

/**
 * These helpers are typed structurally rather than against `WorkspaceApp` /
 * `ApplicationRepository` so the module stays independent of the generated SDK.
 * Generics keep the caller's concrete type flowing through.
 */
type InstalledLike = {
  name: string
  instanceTypeName?: string
  description?: string
  createdAt?: string
}

type CatalogLike = {
  id: number
  name: string
  description?: string | null
  is_installed?: boolean | null
}

const matches = (value: string | null | undefined, needle: string) =>
  (value ?? '').toLowerCase().includes(needle)

/** Lower-cased, trimmed search term. Empty string means "no filter". */
export const normalizeNeedle = (search: string) => search.trim().toLowerCase()

/** Matches only what the card actually shows, so every hit is explainable. */
export const filterInstalled = <T extends InstalledLike>(
  apps: T[],
  needle: string
): T[] =>
  needle
    ? apps.filter(
        (app) =>
          matches(app.name, needle) ||
          matches(app.instanceTypeName, needle) ||
          matches(app.description, needle)
      )
    : apps

/** "Available" is whatever the API flags as not installed — that flag wins. */
export const filterAvailable = <T extends CatalogLike>(
  repos: T[],
  needle: string
): T[] => {
  const notInstalled = repos.filter((repo) => !repo.is_installed)

  return needle
    ? notInstalled.filter(
        (repo) =>
          matches(repo.name, needle) || matches(repo.description, needle)
      )
    : notInstalled
}

const byName = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' })

/**
 * Installed apps carry a real `createdAt`, so newest/oldest sort on that.
 * Sorting is client-side because the board holds at most a handful of apps and
 * both groups must order consistently — the two endpoints behind them don't
 * share an `ordering` vocabulary. Always returns a copy: the inputs come
 * straight from react-query caches.
 */
export const sortInstalled = <T extends InstalledLike>(
  apps: T[],
  ordering: BoardOrdering
): T[] => {
  const sorted = [...apps]
  switch (ordering) {
    case 'name':
      return sorted.sort((a, b) => byName(a.name, b.name))
    case '-name':
      return sorted.sort((a, b) => byName(b.name, a.name))
    case 'newest':
      return sorted.sort(
        (a, b) => Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? '')
      )
    case 'oldest':
      return sorted.sort(
        (a, b) => Date.parse(a.createdAt ?? '') - Date.parse(b.createdAt ?? '')
      )
  }
}

/**
 * Catalog entries have no timestamp, so newest/oldest fall back to catalog id
 * order — higher id is newer. Keeps the control meaningful for both groups
 * instead of silently doing nothing on one of them.
 */
export const sortAvailable = <T extends CatalogLike>(
  repos: T[],
  ordering: BoardOrdering
): T[] => {
  const sorted = [...repos]
  switch (ordering) {
    case 'name':
      return sorted.sort((a, b) => byName(a.name, b.name))
    case '-name':
      return sorted.sort((a, b) => byName(b.name, a.name))
    case 'newest':
      return sorted.sort((a, b) => b.id - a.id)
    case 'oldest':
      return sorted.sort((a, b) => a.id - b.id)
  }
}

/**
 * Groups access rows into a per-application count.
 *
 * Returns a Map rather than a plain object so a missing app is `undefined`
 * (count unknown) instead of `0` (nobody has access) — those mean very
 * different things to an admin, and conflating them is how a failed request
 * ends up rendering a confident zero.
 */
export const countAccessByApplication = (
  rows: { application?: { id?: number } | null }[]
) => {
  const counts = new Map<number, number>()
  rows.forEach((row) => {
    const appId = row.application?.id
    if (typeof appId === 'number') {
      counts.set(appId, (counts.get(appId) ?? 0) + 1)
    }
  })

  return counts
}
